import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../support/store';
import {
  checkJourneyMapReferenceDataLoaded,
  checkJourneyMapsLoaded,
} from '../support/slices/journeyMapsSlice';
import {
  coerceCompletionProvider,
  findJourneyMapProviderByValue,
  mergeJourneyMapProviders,
} from '../support/journeyMap/providers';
import {
  JourneyMap,
  JourneyMapCompletionProvider,
  JourneyMapDraft,
  JourneyMapType,
} from '../types/journeyMap';

export function useJourneyMaps(): JourneyMap[] {
  return useSelector((state: RootState) => state.journeyMaps.maps || []);
}

export function useJourneyMapTypes(): JourneyMapType[] {
  return useSelector((state: RootState) => state.journeyMaps.types || []);
}

export function useJourneyMapAssignmentStatuses() {
  return useSelector((state: RootState) => state.journeyMaps.assignmentStatuses || []);
}

export function useJourneyMapItemStatuses() {
  return useSelector((state: RootState) => state.journeyMaps.itemStatuses || []);
}

export function useJourneyMapCompletionProviders(): JourneyMapCompletionProvider[] {
  return useSelector((state: RootState) => state.journeyMaps.completionProviders || []);
}

export function useJourneyMapCompletionProviderRules(): any[] {
  return useSelector((state: RootState) => state.journeyMaps.completionProviderRules || []);
}

export function useJourneyMapsState() {
  return useSelector((state: RootState) => state.journeyMaps);
}

export function useEnsureJourneyMapsLoaded() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(checkJourneyMapsLoaded() as any);
  }, [dispatch]);
}

export function useEnsureJourneyMapReferenceDataLoaded() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(checkJourneyMapReferenceDataLoaded() as any);
  }, [dispatch]);
}

export function useActiveJourneyMapDraft(): JourneyMapDraft | null {
  return useSelector((state: RootState) => {
    const activeDraftId = state.journeyMapBuilder.activeDraftId;
    return state.journeyMapBuilder.drafts.find((draft) => draft.tempId === activeDraftId) || null;
  });
}

export function useJourneyMapDrafts(): JourneyMapDraft[] {
  return useSelector((state: RootState) => state.journeyMapBuilder.drafts || []);
}

export function useJourneyMapById(id?: number | string | null): JourneyMap | null {
  const journeyMaps = useJourneyMaps();

  return useMemo(() => {
    if (id == null) return null;
    return journeyMaps.find((map) => String(map.id) === String(id)) || null;
  }, [journeyMaps, id]);
}

export function useJourneyMapTypeById(id?: number | string | null): JourneyMapType | null {
  const types = useJourneyMapTypes();

  return useMemo(() => {
    if (id == null) return null;
    return types.find((type) => String(type.id) === String(id)) || null;
  }, [types, id]);
}

export function useJourneyMapCompletionProviderByName(
  providerName?: string | null,
): JourneyMapCompletionProvider | null {
  const providers = useJourneyMapCompletionProviders();

  return useMemo(() => {
    if (!providerName) return null;

    const normalizedInput = String(providerName).trim().toLowerCase();
    const normalizedInputKey = normalizedInput.replace(/\s+/g, '_');

    return (
      providers.find((provider) => {
        const displayName = String(provider?.display_name || '').trim().toLowerCase();
        const providerKey = String(provider?.provider_key || '')
          .trim()
          .toLowerCase();
        return displayName === normalizedInput || providerKey === normalizedInputKey;
      }) || null
    );
  }, [providers, providerName]);
}

export function useJourneyMapResolvedProviders() {
  const rawProviders = useJourneyMapCompletionProviders();
  const rawRules = useJourneyMapCompletionProviderRules();

  return useMemo(() => {
    const normalizedProviders = (Array.isArray(rawProviders) ? rawProviders : [])
      .map((provider) => coerceCompletionProvider(provider, rawRules))
      .filter(Boolean);

    return mergeJourneyMapProviders(normalizedProviders as JourneyMapCompletionProvider[]);
  }, [rawProviders, rawRules]);
}

export function useJourneyMapResolvedProviderByName(providerName?: string | null) {
  const providers = useJourneyMapResolvedProviders();

  return useMemo(() => {
    return findJourneyMapProviderByValue(providers, providerName);
  }, [providers, providerName]);
}